import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth/useAuth';
import {
  Sparkles,
  Package,
  CalendarPlus,
  FolderPlus,
  ArrowRight,
  ArrowUpRight,
} from 'lucide-react';
import { api } from '@/lib/api/client';
import { brandsApi } from '@/features/brands/api';
import { queryKeys } from '@/lib/api/queryKeys';
import type { XkEvent, XkEventGroup, XkStation } from '@/lib/api/types';
import { Skeleton } from '@/components/ui/Skeleton';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { useT } from '@/lib/i18n/useT';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { HelpHint } from '@/features/help/HelpHint';
import { useFormPanel } from '@/components/layout/formPanelStore';
import { useRightSidebar } from '@/components/layout/RightSidebar';
import { useHasFeature } from '@/lib/permissions/useCompanyFeatures';
import { useAIContext } from '@/features/ai-assistant/useAIContext';
import { useAIStream } from '@/features/ai-assistant/useAIStream';
import { atlediApi } from '@/features/insights/atlediApi';
import { cn } from '@/lib/cn';

/**
 * BrandProfilePage — simplified brand home (PRD Xotski v2.0 §7.1, Entity Home).
 *
 * A focused landing surface: an Atledi greeting hero, three quick-create
 * action cards, a "jump back into your projects" list, and an Atledi composer.
 * The detailed catalogs (projects, xotskies) now live on their own sidebar
 * pages (/brand/:id/projects, /brand/:id/xotskies), so this page stays light.
 *
 * Creating a project opens the right-panel form (assistant rail) instead of a
 * modal — see `formPanelStore`.
 */

type ProjectStatusKind = 'live' | 'planning' | 'wrapped';

interface ProjectRow {
  id: number;
  name: string;
  events: number;
  stations: number;
  status: ProjectStatusKind;
}

export function BrandProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const brandId = Number(id);
  const t = useT();
  const { role, user } = useAuth();
  const openForm = useFormPanel((s) => s.openForm);

  // Access guard — mirrors the previous page: bounce non-members out before
  // any brand data loads. Admin/master bypass the check.
  const isFullRole = role === 'admin' || role === 'master';
  const accessibleBrandsQ = useQuery({
    queryKey: queryKeys.brands.all,
    queryFn: () => brandsApi.list(),
  });
  const accessibleBrands = accessibleBrandsQ.data?.items ?? [];
  const hasAccess =
    isFullRole ||
    (!!accessibleBrandsQ.data && accessibleBrands.some((b) => b.id === brandId));
  const accessChecked = isFullRole || !!accessibleBrandsQ.data;

  useEffect(() => {
    if (accessChecked && !hasAccess) navigate('/', { replace: true });
  }, [accessChecked, hasAccess, navigate]);

  const brandQ = useQuery({
    queryKey: queryKeys.brands.detail(brandId),
    queryFn: () => brandsApi.get(brandId),
    enabled: Number.isFinite(brandId) && hasAccess,
  });
  const brand = brandQ.data ?? null;
  useFavoritable(brand ? `${t('sidebar.home')} · ${brand.name}` : null);

  const groupsQ = useQuery({
    queryKey: queryKeys.eventGroups.byBrand(brandId),
    queryFn: () =>
      api.list<XkEventGroup>('/xk-event-group', { filter: { companyId: brandId } }),
    enabled: Number.isFinite(brandId) && hasAccess,
  });
  const eventsQ = useQuery({
    queryKey: queryKeys.events.byBrand(brandId),
    queryFn: () => api.list<XkEvent>('/xk-event', { filter: { companyId: brandId } }),
    enabled: Number.isFinite(brandId) && hasAccess,
  });
  const stationsQ = useQuery({
    queryKey: queryKeys.stations.byBrand(brandId),
    queryFn: () =>
      api.list<XkStation>('/xk-station', { filter: { companyId: brandId } }),
    enabled: Number.isFinite(brandId) && hasAccess,
  });

  const projects: ProjectRow[] = useMemo(() => {
    if (!groupsQ.data || !eventsQ.data || !stationsQ.data) return [];
    const eventsByGroup = new Map<number, XkEvent[]>();
    for (const e of eventsQ.data.items) {
      const list = eventsByGroup.get(e.eventGroupId) ?? [];
      list.push(e);
      eventsByGroup.set(e.eventGroupId, list);
    }
    return groupsQ.data.items.map((g) => {
      const events = eventsByGroup.get(g.id) ?? [];
      const eventIds = new Set(events.map((e) => e.id));
      const stations = stationsQ.data!.items.filter((s) => eventIds.has(s.eventId));
      return {
        id: g.id,
        name: g.name,
        events: events.length,
        stations: stations.length,
        status: deriveStatus(events),
      };
    });
  }, [groupsQ.data, eventsQ.data, stationsQ.data]);

  const greetingName =
    user?.firstname?.trim() || brand?.name || t('entityHome.atledi.youFallback');

  // Quick-create cards — all three host their form in the right panel (no
  // dialogs, no leaving the home). Creating a project jumps into it; xotski/
  // event stay on the home so the user can keep adding.
  const addProject = () =>
    openForm({
      kind: 'project',
      mode: 'create',
      title: t('brand.newProject'),
      context: { brandId },
      onDone: (g) => {
        const created = g as { id: number } | undefined;
        if (created?.id) navigate(`/projects/${created.id}`);
      },
    });
  const addXotski = () =>
    openForm({
      kind: 'xotski',
      mode: 'create',
      title: t('brandHome.addXotskies'),
      context: { brandId },
      onDone: () => navigate(`/brand/${brandId}/xotskies`),
    });
  const addEvent = () =>
    openForm({
      kind: 'event',
      mode: 'create',
      title: t('brandHome.addEvents'),
      context: { brandId },
      onDone: (e) => {
        const ev = e as { id: number; eventGroupId: number } | undefined;
        if (ev?.id && ev?.eventGroupId) {
          navigate(`/projects/${ev.eventGroupId}/events/${ev.id}`);
        }
      },
    });

  if (!accessChecked) {
    return (
      <div className="page space-y-4">
        <Skeleton className="mx-auto h-40 w-full max-w-2xl rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    );
  }
  if (!hasAccess) return null;

  return (
    <div className="page mx-auto max-w-3xl">
      <div className="mb-4 flex items-center justify-between">
        <Breadcrumbs items={[{ label: brand?.name ?? t('breadcrumbs.brand') }]} />
        <HelpHint section="brand-detail" />
      </div>

      {/* ── Atledi hero ─────────────────────────────────────── */}
      <AtlediHero brandName={brand?.name} greetingName={greetingName} />

      {/* ── Quick-create action cards ───────────────────────── */}
      <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <ActionCard
          icon={<Package className="h-5 w-5" />}
          title={t('brandHome.addXotskies')}
          subtitle={t('brandHome.addXotskiesHint')}
          onClick={addXotski}
        />
        <ActionCard
          icon={<CalendarPlus className="h-5 w-5" />}
          title={t('brandHome.addEvents')}
          subtitle={t('brandHome.addEventsHint')}
          onClick={addEvent}
          dataTour="quick-add-events"
        />
        <ActionCard
          icon={<FolderPlus className="h-5 w-5" />}
          title={t('brandHome.addProject')}
          subtitle={t('brandHome.addProjectHint')}
          onClick={addProject}
          dataTour="quick-add-project"
        />
      </div>

      {/* ── Jump back into your projects ────────────────────── */}
      <section className="mt-9">
        <h2
          className="mb-3 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {t('brandHome.jumpBack')}
        </h2>
        {groupsQ.isLoading ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-16 rounded-xl" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <button
            type="button"
            onClick={addProject}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-4 py-6 text-sm text-[var(--color-xk-text-muted)] transition-colors hover:border-[var(--color-xk-accent)] hover:text-[var(--color-xk-lavender)]"
          >
            <FolderPlus className="h-4 w-4" />
            {t('brand.createProject')}
          </button>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {projects.slice(0, 6).map((p) => (
              <ProjectJumpCard
                key={p.id}
                project={p}
                accent={brand?.secondaryColor ?? null}
                onClick={() => navigate(`/projects/${p.id}`)}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── Atledi composer ─────────────────────────────────── */}
      <div className="mt-6">
        <AtlediComposer />
      </div>
    </div>
  );
}

// ─── Atledi hero ─────────────────────────────────────────────────────────────

function AtlediHero({
  brandName,
  greetingName,
}: {
  brandName?: string;
  greetingName: string;
}) {
  const t = useT();
  const language = usePrefsStore((s) => s.language);
  const locale: 'en' | 'es' = language === 'es' ? 'es' : 'en';

  const live = useQuery({
    queryKey: ['atledi-welcome', brandName ?? '', locale],
    queryFn: () => atlediApi.welcome({ brandName, locale }),
    staleTime: 4 * 60 * 60 * 1000,
    retry: false,
  });

  const dateLabel = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(locale === 'es' ? 'es-ES' : 'en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      })
        .format(new Date())
        .toUpperCase();
    } catch {
      return '';
    }
  }, [locale]);

  const slot = (() => {
    const h = new Date().getHours();
    return h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening';
  })();
  const localGreeting = t(`entityHome.atledi.greeting.${slot}`).replace(
    '{name}',
    greetingName,
  );
  const greeting = live.data?.greeting ?? localGreeting;

  return (
    <section className="flex flex-col items-center text-center">
      {/* Glowing Atledi orb */}
      <div className="relative mb-5 grid h-16 w-16 place-items-center">
        <span
          aria-hidden
          className="absolute inset-0 rounded-full bg-[var(--color-xk-accent)] opacity-30 blur-2xl"
        />
        <span className="relative grid h-12 w-12 place-items-center rounded-full bg-[var(--color-xk-accent)] text-[var(--color-fg-on-accent)] shadow-[0_0_40px_var(--color-accent-30)]">
          <Sparkles className="h-6 w-6" />
        </span>
      </div>

      <p className="mb-3 text-[11px] font-extrabold uppercase tracking-[0.2em] text-[var(--color-xk-accent)]">
        {t('entityHome.atledi.label')}
        {dateLabel ? ` · ${dateLabel}` : ''}
      </p>
      <h1
        className="text-3xl font-black tracking-tight text-[var(--color-xk-lavender)] sm:text-4xl"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        {t('brandHome.cooking').replace('{name}', greetingName)}
      </h1>
      <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-[var(--color-xk-text-muted)]">
        {greeting}
      </p>
    </section>
  );
}

// ─── Action card ─────────────────────────────────────────────────────────────

function ActionCard({
  icon,
  title,
  subtitle,
  onClick,
  dataTour,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  dataTour?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-tour={dataTour}
      className="group flex flex-col items-start gap-2 rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-4 text-left transition-all hover:-translate-y-0.5 hover:border-[var(--color-xk-accent)]"
    >
      <span className="grid h-9 w-9 place-items-center rounded-lg bg-[var(--color-accent-10)] text-[var(--color-xk-accent)]">
        {icon}
      </span>
      <span
        className="mt-1 text-sm font-extrabold text-[var(--color-xk-lavender)]"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        {title}
      </span>
      <span className="text-[11px] leading-snug text-[var(--color-xk-text-muted)]">
        {subtitle}
      </span>
    </button>
  );
}

// ─── Project jump card ───────────────────────────────────────────────────────

const STATUS_META: Record<ProjectStatusKind, { labelKey: string; color: string }> = {
  live: { labelKey: 'common.live', color: 'var(--color-success)' },
  planning: { labelKey: 'brandHome.status.planning', color: 'var(--color-warn)' },
  wrapped: { labelKey: 'brandHome.status.wrapped', color: 'var(--color-xk-text-muted)' },
};

function ProjectJumpCard({
  project,
  accent,
  onClick,
}: {
  project: ProjectRow;
  accent: string | null;
  onClick: () => void;
}) {
  const t = useT();
  const meta = STATUS_META[project.status];
  const ring = accent || 'var(--color-xk-accent)';
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex items-center gap-3 rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-4 py-3 text-left transition-all hover:-translate-y-0.5 hover:border-[var(--color-xk-accent)]"
    >
      <span
        aria-hidden
        className="grid h-9 w-9 flex-none place-items-center rounded-lg text-sm font-black text-[#0A0A0A]"
        style={{ backgroundColor: ring, fontFamily: 'var(--font-display)' }}
      >
        {project.name.charAt(0).toUpperCase()}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-extrabold text-[var(--color-xk-lavender)]">
          {project.name}
        </span>
        <span className="block truncate text-[11px] text-[var(--color-xk-text-muted)] tabular-nums">
          {project.events} {t('subbrand.events')} · {project.stations}{' '}
          {t('subbrand.active')}
        </span>
      </span>
      <span
        className="flex-none rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-widest"
        style={{ backgroundColor: `color-mix(in srgb, ${meta.color} 15%, transparent)`, color: meta.color }}
      >
        {t(meta.labelKey)}
      </span>
      <ArrowUpRight className="h-4 w-4 flex-none text-[var(--color-xk-text-muted)] transition-colors group-hover:text-[var(--color-xk-accent)]" />
    </button>
  );
}

// ─── Atledi composer ─────────────────────────────────────────────────────────

function AtlediComposer() {
  const t = useT();
  const [text, setText] = useState('');
  const setRightOpen = useRightSidebar((s) => s.setOpen);
  const hasFeature = useHasFeature('aiAssistant');
  const ctx = useAIContext();
  const { send } = useAIStream();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const msg = text.trim();
    // Open the assistant rail regardless; when the plan lacks the feature the
    // panel shows the paywall instead of streaming.
    setRightOpen(true);
    if (msg && hasFeature) void send({ ctx, userMessage: msg });
    setText('');
  };

  return (
    <form
      onSubmit={submit}
      className="flex items-center gap-2 rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-4 py-2.5"
    >
      <Sparkles className="h-4 w-4 flex-none text-[var(--color-xk-accent)]" />
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t('brandHome.askAtledi')}
        className="min-w-0 flex-1 bg-transparent text-sm text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:outline-none"
      />
      <button
        type="submit"
        aria-label={t('brandHome.askAtledi')}
        className={cn(
          'grid h-8 w-8 flex-none place-items-center rounded-lg transition-colors',
          'bg-[var(--color-accent-10)] text-[var(--color-xk-accent)] hover:bg-[var(--color-xk-accent)] hover:text-[var(--color-fg-on-accent)]',
        )}
      >
        <ArrowRight className="h-4 w-4" />
      </button>
    </form>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────────

/** Derive a coarse project status from its events' live window. */
function deriveStatus(events: XkEvent[]): ProjectStatusKind {
  if (events.length === 0) return 'planning';
  const now = Date.now();
  let hasLive = false;
  let allWrapped = true;
  for (const e of events) {
    if (e.eventStatus !== 'Active') continue;
    const start = e.startDate ? new Date(e.startDate).getTime() : null;
    const end = e.endDate ? new Date(e.endDate).getTime() : null;
    if (start != null && end != null && now >= start && now <= end) hasLive = true;
    if (end != null && now <= end) allWrapped = false;
    if (start == null || end == null) allWrapped = false;
  }
  if (hasLive) return 'live';
  if (allWrapped) return 'wrapped';
  return 'planning';
}
